import { useEffect, useState, useSyncExternalStore } from 'react'
import { sha256Hex } from '@/shared/lib/hash'
import { AlertTriangle, Check, Loader2, Mic, RefreshCw, Square } from 'lucide-react'
import { ApiError, explain } from '@/shared/api/problem'
import { Note } from '@/shared/ui/states'
import { cn } from '@/lib/utils'
import { completeMedia, reserveMedia } from './api'

/**
 * The interview recording, living OUTSIDE React.
 *
 * ## Why the recorder is a module singleton
 *
 * `broadcastSession.ts` in `features/coverage` was written after this project
 * shipped a live camera inside a `useEffect`: navigating away ran the cleanup,
 * the room disconnected and the panel went on saying "on air". An interview that
 * loses its audio because the interviewer checked the quota screen — or because
 * a re-render remounted a panel — is the same bug wearing a different hat, and
 * the audio is the thing a back-check listens to. So the `MediaRecorder`, the
 * `MediaStream` and the finished clips live here, at module scope, and React
 * only ever subscribes.
 *
 * Exactly four things end a recording: pressing Stop, submitting the interview,
 * closing the tab, and the microphone track dying. Unmounting a panel is not one
 * of them.
 *
 * ## Why the clips outlive the interview
 *
 * The response is created by `POST /surveys/{id}/responses`, and only then does a
 * `responseId` exist to reserve media against. So the order is always
 * submit-then-upload, and a failed upload must not cost the interview: the
 * answers are already stored server-side, the clip sits here in `held` state and
 * retries on its own. The screen says the interview is saved and the recording is
 * still going up, because those are two different facts.
 *
 * ## Why this file does not import the session or the queue
 *
 * `captureSession` drives this module and `captureQueue` reuses {@link uploadClip}
 * to finish an offline interview's audio after its response is finally created.
 * Both point INWARDS. An import back the other way would close a cycle through
 * `useCapture`, and the module that must never fail to initialise is this one.
 */

/**
 * `PHOTO` is here rather than in a file of its own because it takes the same
 * road: a `MEDIA_CAPTURE` question's image is reserved, PUT and completed by the
 * identical three calls, and it is held against the same interview until a
 * response exists to hang it on. The only difference is that it carries a
 * `questionId` and audio does not.
 */
export type ClipKind = 'AUDIO_INTERVIEW' | 'AUDIO_CONSENT' | 'PHOTO'

/** `refused` is the server saying no and meaning it; `failed` is worth retrying. */
export type ClipState = 'held' | 'uploading' | 'stored' | 'refused' | 'failed'

export interface HeldClip {
  /** Local only. The server's `mediaId` does not exist until a reservation is made. */
  id: string
  kindCode: ClipKind
  /** Set only for a `MEDIA_CAPTURE` answer — it is what links the image to the question. */
  questionId: string | null
  blob: Blob
  contentType: string
  sizeBytes: number
  durationSeconds: number
  capturedAt: string
  state: ClipState
  message: string | null
  mediaId: string | null
  /**
   * The presigned PUT the reservation minted, kept beside the `mediaId`.
   *
   * Without both of these a retry restarts at `reserveMedia` and leaves a second
   * RESERVED media row against the same interview that nothing will ever
   * complete — three retries, three rows, one clip.
   */
  uploadUrl: string | null
}

type RecorderPhase = 'off' | 'requesting' | 'recording' | 'stopping'

export interface RecorderSnapshot {
  phase: RecorderPhase
  /** What is being recorded RIGHT NOW. Null unless `phase === 'recording'`. */
  kindCode: ClipKind | null
  /** Epoch ms. Elapsed is derived so it survives every remount. */
  startedAt: number | null
  micError: string | null
  deviceLabel: string | null
  clips: HeldClip[]
}

const IDLE: RecorderSnapshot = {
  phase: 'off', kindCode: null, startedAt: null, micError: null, deviceLabel: null, clips: [],
}

let snapshot: RecorderSnapshot = IDLE
const listeners = new Set<() => void>()

let stream: MediaStream | null = null
let recorder: MediaRecorder | null = null
let chunks: Blob[] = []
let startedAtMs = 0
let activeKind: ClipKind | null = null

function set(patch: Partial<RecorderSnapshot>) {
  // A new object every time — `useSyncExternalStore` compares by identity, and a
  // mutated-in-place snapshot renders nothing at all.
  snapshot = { ...snapshot, ...patch }
  listeners.forEach((l) => l())
}

export function subscribeRecorder(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function getRecorderSnapshot(): RecorderSnapshot {
  return snapshot
}

/** React's only window onto the recorder. No state, deliberately. */
export function useRecorder(): RecorderSnapshot {
  return useSyncExternalStore(subscribeRecorder, getRecorderSnapshot, getRecorderSnapshot)
}

export function isRecording(): boolean {
  return snapshot.phase === 'recording' || snapshot.phase === 'requesting'
}

// ── the microphone ──────────────────────────────────────────────────────────

/**
 * Can this browser, on this origin, hand over a microphone at all?
 *
 * `getUserMedia` is gated on a SECURE ORIGIN. `https://…` works, and so does
 * `http://localhost` — which the spec treats as trustworthy — but a bare LAN
 * address like `http://192.168.1.20:5173` does NOT, and `navigator.mediaDevices`
 * is simply `undefined` there rather than throwing something readable. Somebody
 * on this project already lost an afternoon to that, so the failure says which
 * address to use instead of "recording unavailable".
 */
export function micPreflight(): { ok: boolean; reason: string | null } {
  if (typeof window === 'undefined') return { ok: false, reason: null }
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      reason: 'This browser will not open a microphone on a plain http:// address that is not '
        + 'localhost. Open the console over https, or on the machine itself at localhost. '
        + 'A phone on the office wifi needs the https address — the certificate warning is '
        + 'expected and the connection is still encrypted.',
    }
  }
  if (typeof MediaRecorder === 'undefined') {
    return { ok: false, reason: 'This browser has no MediaRecorder, so it cannot record audio.' }
  }
  return { ok: true, reason: null }
}

/**
 * The container, chosen by what the browser will actually produce.
 *
 * Chrome and Firefox give WebM/Opus; Safari and every iOS browser give MP4/AAC
 * and refuse WebM outright. Passing an unsupported `mimeType` to `MediaRecorder`
 * throws `NotSupportedError` at construction — so the list is probed rather than
 * assumed, and the empty string at the end lets the browser pick its own default
 * instead of failing. Whatever wins is sent to `reserveMedia` as the real
 * `contentType`: the server's policy check is on the type that will be stored,
 * not on the one we hoped for.
 */
const CANDIDATE_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  '',
]

function pickMimeType(): string {
  for (const type of CANDIDATE_TYPES) {
    if (type === '') return ''
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

function releaseMicrophone() {
  stream?.getTracks().forEach((t) => t.stop())
  stream = null
}

/**
 * Start recording.
 *
 * Called by `captureSession` AFTER consent and never before — a clip that starts
 * while the consent script is still being read is a recording the respondent has
 * not yet agreed to, which is the one thing the audio disclosure exists to
 * prevent.
 */
export async function startRecording(kindCode: ClipKind): Promise<void> {
  if (isRecording()) return
  const check = micPreflight()
  if (!check.ok) { set({ phase: 'off', micError: check.reason }); return }

  set({ phase: 'requesting', micError: null, kindCode })
  try {
    // Release first: on most phones a second getUserMedia while the first track
    // is still live fails with NotReadableError, which reads as "another app has
    // the microphone" and blames something the interviewer has already closed.
    releaseMicrophone()
    const opened = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream = opened
    chunks = []
    const mimeType = pickMimeType()
    const rec = mimeType ? new MediaRecorder(opened, { mimeType }) : new MediaRecorder(opened)
    recorder = rec
    activeKind = kindCode
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
    rec.onerror = () => {
      set({ phase: 'off', kindCode: null, startedAt: null, micError: 'The recording stopped unexpectedly.' })
      releaseMicrophone()
    }
    // A one-second timeslice, not a single blob at the end: a tab killed
    // mid-interview then has most of the audio in `chunks` rather than none of
    // it, and the browser flushes to disk as it goes instead of holding an hour
    // of PCM in memory on a cheap phone.
    rec.start(1000)
    startedAtMs = Date.now()
    set({
      phase: 'recording',
      kindCode,
      startedAt: startedAtMs,
      deviceLabel: opened.getAudioTracks()[0]?.label ?? null,
    })
  } catch (e) {
    releaseMicrophone()
    set({ phase: 'off', kindCode: null, startedAt: null, micError: micProblem(e) })
  }
}

/**
 * Stop, and hold the clip.
 *
 * The duration is WALL CLOCK, not read back off the blob. A WebM stream written
 * by `MediaRecorder` carries no duration in its header — every player reports
 * `Infinity` until it has seeked the whole file — so reading it back would send
 * the server a null or a nonsense number, and `AUDIO_TOO_SHORT` is a real
 * integrity rule that would then fire on honest interviews.
 */
export function stopRecording(): Promise<HeldClip | null> {
  const rec = recorder
  const kind = activeKind
  if (!rec || !kind || rec.state === 'inactive') {
    releaseMicrophone()
    set({ phase: 'off', kindCode: null, startedAt: null })
    return Promise.resolve(null)
  }
  set({ phase: 'stopping' })
  return new Promise<HeldClip | null>((resolve) => {
    rec.onstop = () => {
      const contentType = rec.mimeType || 'audio/webm'
      const blob = new Blob(chunks, { type: contentType })
      chunks = []
      recorder = null
      activeKind = null
      // The microphone light goes out HERE, not on unmount. A tab still holding
      // the mic after the interview ended is the coverage bug all over again.
      releaseMicrophone()
      const clip: HeldClip = {
        id: newLocalId(),
        kindCode: kind,
        questionId: null,
        blob,
        // The browser appends codec parameters (`audio/webm;codecs=opus`) and the
        // server's policy matches on the family, so the full string is sent as-is
        // rather than trimmed to something we made up.
        contentType,
        sizeBytes: blob.size,
        durationSeconds: Math.max(1, Math.round((Date.now() - startedAtMs) / 1000)),
        capturedAt: new Date(startedAtMs).toISOString(),
        state: 'held',
        message: null,
        mediaId: null,
        uploadUrl: null,
      }
      set({ phase: 'off', kindCode: null, startedAt: null, clips: [...snapshot.clips, clip] })
      resolve(clip)
    }
    rec.stop()
  })
}

/** Abandon a recording without keeping it — a restarted interview, a mistake. */
export function cancelRecording(): void {
  const rec = recorder
  if (rec && rec.state !== 'inactive') {
    rec.onstop = null
    rec.stop()
  }
  chunks = []
  recorder = null
  activeKind = null
  releaseMicrophone()
  set({ phase: 'off', kindCode: null, startedAt: null })
}

/**
 * A photo taken for a `MEDIA_CAPTURE` question.
 *
 * Held exactly like a recording, and for the same reason: `reserveMedia` needs a
 * `responseId`, and the response does not exist until the interview is
 * submitted. An `<input type="file" capture>` is used rather than a second
 * `getUserMedia` preview — the phone's own camera app focuses, exposes and
 * compresses better than anything this screen would build, and it does not
 * contend with the microphone the interview is already holding.
 */
export function addPhotoClip(questionId: string, file: File): HeldClip {
  const clip: HeldClip = {
    id: newLocalId(),
    kindCode: 'PHOTO',
    questionId,
    blob: file,
    contentType: file.type || 'image/jpeg',
    sizeBytes: file.size,
    durationSeconds: 0,
    capturedAt: new Date(file.lastModified || Date.now()).toISOString(),
    state: 'held',
    message: null,
    mediaId: null,
    uploadUrl: null,
  }
  // One photo per question: retaking replaces rather than accumulates, or an
  // interviewer who retook a blurry shot uploads both and the coder gets two.
  set({
    clips: [...snapshot.clips.filter(
      (c) => !(c.kindCode === 'PHOTO' && c.questionId === questionId),
    ), clip],
  })
  return clip
}

export function photoFor(questionId: string): HeldClip | null {
  return snapshot.clips.find((c) => c.kindCode === 'PHOTO' && c.questionId === questionId) ?? null
}

export function hasClip(kindCode: ClipKind): boolean {
  return snapshot.clips.some((c) => c.kindCode === kindCode)
}

/**
 * Hand every not-yet-stored clip to the caller and forget them here.
 *
 * Used when the interview goes into the offline queue: the blobs move to
 * IndexedDB under the interview's idempotency key and come back out when the
 * queued response is finally created and has an id to hang media on. Used again
 * when the interviewer moves to the next respondent while a clip is still
 * unsent — `resetRecorder` is about to clear this list, and a clip that was only
 * ever in memory is gone with it.
 */
export function takeUnstoredClips(): HeldClip[] {
  const taken = snapshot.clips.filter((c) => c.state !== 'stored')
  set({ clips: snapshot.clips.filter((c) => c.state === 'stored') })
  return taken
}

/** A finished interview's clips are gone; the next one starts empty. */
export function resetRecorder(): void {
  cancelRecording()
  set({ ...IDLE })
}

// ── the reserve / upload / complete flow ────────────────────────────────────

export interface UploadOutcome {
  state: Exclude<ClipState, 'held' | 'uploading'>
  message: string | null
  mediaId: string | null
  /** Carried back so a retry can resume at the PUT instead of reserving again. */
  uploadUrl: string | null
}

/** The shape the flow needs — narrower than `HeldClip` so the queue can rehydrate one. */
export interface UploadableClip {
  kindCode: ClipKind
  questionId?: string | null
  blob: Blob
  contentType: string
  sizeBytes: number
  durationSeconds: number
  capturedAt: string
  /** Both set together, by a reservation that succeeded on an earlier attempt. */
  mediaId?: string | null
  uploadUrl?: string | null
}

/**
 * Reserve, PUT, complete. One clip, three calls, and only the middle one is big.
 *
 * The size cap is enforced SERVER-SIDE before the upload URL is minted, which is
 * the whole reason the reservation is a separate round trip: an interviewer on a
 * bus learns a 200 MB clip is refused now, rather than after spending the data on
 * it. That refusal is a 4xx and is final — retrying the same bytes cannot change
 * the answer — so it comes back as `refused` and the UI stops offering Retry.
 *
 * A failed PUT is the opposite: the network moved, the bytes are still here, and
 * the interview itself is already stored. That comes back as `failed` and is
 * retried.
 *
 * ## A retry resumes at the PUT
 *
 * A clip that already holds a `mediaId` and an `uploadUrl` skips the reservation
 * entirely. Reserving again on every attempt is what left three RESERVED media
 * rows against one interview after three retries, two of which were never
 * completed — one clip, one row, however many attempts it takes.
 */
export async function uploadClip(
  responseId: string,
  clip: UploadableClip,
): Promise<UploadOutcome> {
  let mediaId = clip.mediaId ?? null
  let uploadUrl = clip.uploadUrl ?? null
  const resumed = mediaId !== null && uploadUrl !== null

  if (!resumed) {
    const first = await reserve(responseId, clip)
    if (!first.ok) return first.outcome
    mediaId = first.mediaId
    uploadUrl = first.uploadUrl
  }

  let put = await putBytes(uploadUrl!, clip)
  if (!put.ok && put.stale && resumed) {
    // A presigned URL expires — typically an hour — so a clip that has been
    // waiting for signal overnight will be refused by the store with a 403 that
    // says nothing readable. Re-reserving ONCE is the trade: it can mint a second
    // media row when the 403 was really a policy refusal, and an uncompleted
    // RESERVED row is something a cleanup can find, whereas a clip whose only
    // upload URL has expired can never go up at all.
    const again = await reserve(responseId, clip)
    if (!again.ok) return again.outcome
    mediaId = again.mediaId
    uploadUrl = again.uploadUrl
    put = await putBytes(uploadUrl, clip)
  }
  // The URL is kept on a failure rather than discarded: the next attempt resumes
  // on it, and only an expiry sends it back through `reserveMedia`.
  if (!put.ok) return { state: 'failed', message: put.message, mediaId, uploadUrl }

  try {
    // The hash is computed over the bytes that were actually PUT, so a
    // truncated upload is caught by the server rather than accepted as the
    // interview's audio. `crypto.subtle` needs a secure origin too — the same
    // one `getUserMedia` needed to produce this blob in the first place.
    const checksumSha256 = await sha256Hex(clip.blob)
    const stored = await completeMedia(responseId, mediaId!, { checksumSha256 })
    return { state: 'stored', message: null, mediaId: stored.id, uploadUrl: null }
  } catch (e) {
    const final = e instanceof ApiError && e.status >= 400 && e.status < 500
    return {
      state: final ? 'refused' : 'failed',
      message: explain(e),
      mediaId,
      // A completion that failed on the network is retried against the SAME
      // reservation. It re-sends the bytes to do it, which costs data on a bus
      // and is still cheaper than a media row per attempt.
      uploadUrl: final ? null : uploadUrl,
    }
  }
}

type Reservation =
  | { ok: true; mediaId: string; uploadUrl: string }
  | { ok: false; outcome: UploadOutcome }

async function reserve(responseId: string, clip: UploadableClip): Promise<Reservation> {
  try {
    const reserved = await reserveMedia(responseId, {
      kindCode: clip.kindCode,
      questionId: clip.questionId ?? null,
      contentType: clip.contentType,
      sizeBytes: clip.sizeBytes,
      durationSeconds: clip.durationSeconds,
      capturedAt: clip.capturedAt,
    })
    return { ok: true, mediaId: reserved.mediaId, uploadUrl: reserved.uploadUrl }
  } catch (e) {
    // A 4xx here is the policy speaking — too large, wrong content type, audio
    // not permitted on this poll. Nothing about a retry changes any of those.
    const final = e instanceof ApiError && e.status >= 400 && e.status < 500
    return {
      ok: false,
      outcome: {
        state: final ? 'refused' : 'failed',
        message: explain(e),
        mediaId: null,
        uploadUrl: null,
      },
    }
  }
}

type PutOutcome = { ok: true } | { ok: false; stale: boolean; message: string }

async function putBytes(uploadUrl: string, clip: UploadableClip): Promise<PutOutcome> {
  try {
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      // The presigned URL is signed for this content type; sending a different
      // one — or none, letting the browser guess — is a signature mismatch and
      // the store answers 403 with an XML body nobody can read.
      headers: { 'Content-Type': clip.contentType },
      body: clip.blob,
    })
    if (!put.ok) {
      return {
        ok: false,
        // An expired or mis-signed URL is a 401/403 from the store itself, and it
        // is the one PUT failure a fresh reservation can fix.
        stale: put.status === 401 || put.status === 403,
        message: `The storage service refused the upload (${put.status}). The interview is `
          + 'saved; the recording will be retried.',
      }
    }
    return { ok: true }
  } catch (e) {
    return {
      ok: false,
      stale: false,
      message: `${explain(e)} The interview is saved; only the recording is still going up.`,
    }
  }
}

/** Every held clip, against a response that now exists. Independent per clip. */
export async function uploadHeldClips(responseId: string): Promise<void> {
  for (const clip of snapshot.clips) {
    if (clip.state === 'stored' || clip.state === 'uploading') continue
    patchClip(clip.id, { state: 'uploading', message: null })
    const outcome = await uploadClip(responseId, clip)
    patchClip(clip.id, {
      state: outcome.state,
      message: outcome.message,
      mediaId: outcome.mediaId,
      // Kept so the retry resumes on this reservation rather than minting another.
      uploadUrl: outcome.uploadUrl,
    })
  }
}

function patchClip(id: string, patch: Partial<HeldClip>) {
  set({ clips: snapshot.clips.map((c) => (c.id === id ? { ...c, ...patch } : c)) })
}

function newLocalId(): string {
  return typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `clip-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function micProblem(e: unknown): string {
  switch ((e as { name?: string } | null)?.name) {
    case 'NotAllowedError':
      return 'Microphone permission was refused. Allow it for this site, then start the '
        + 'recording again — the interview itself is unaffected.'
    case 'NotFoundError':
      return 'No microphone was found on this device.'
    case 'NotReadableError':
      return 'The microphone is already in use by another app. Close it and try again.'
    default:
      return `Could not open the microphone: ${explain(e)}`
  }
}

/**
 * Closing the tab releases the microphone.
 *
 * `pagehide`, not `beforeunload`: the latter does not fire reliably on mobile
 * Safari, which is exactly the platform most likely to be holding the mic in a
 * field interview. Whatever has been captured so far is lost either way — there
 * is no response id to upload it against — but the light goes out.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    stream?.getTracks().forEach((t) => t.stop())
  })
}

// ── the view ────────────────────────────────────────────────────────────────

/** Seconds since `startedAt`, ticking. Derived, so navigation cannot lose it. */
function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (startedAt == null) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [startedAt])
  return startedAt == null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/**
 * The one thing an interviewer must never be unsure about.
 *
 * Sticky, red, animated and carrying the running clock. It sits above the
 * question, not beside a control, because "am I recording" is a question that
 * gets asked while looking at the respondent rather than at the screen.
 */
export function RecordingBeacon() {
  const rec = useRecorder()
  const elapsed = useElapsed(rec.startedAt)
  if (rec.phase !== 'recording' && rec.phase !== 'stopping') return null
  const consent = rec.kindCode === 'AUDIO_CONSENT'
  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-erp-sm text-red-800"
    >
      <span className="relative flex h-3 w-3 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600" />
      </span>
      <span className="font-semibold uppercase tracking-wide">
        {rec.phase === 'stopping' ? 'Finishing' : 'Recording'}
      </span>
      <span className="tabular-nums font-mono">{clock(elapsed)}</span>
      <span className="text-erp-xs text-red-700/80 truncate">
        {consent ? 'consent statement' : 'the interview'}
      </span>
    </div>
  )
}

/**
 * The recorder's controls and the state of every clip it has produced.
 *
 * `responseId` is null until the interview is submitted, and that is not an
 * error state — it is the ordinary order of events. Retry only appears once
 * there is something to upload against.
 */
export function AudioRecorder({
  mode, kindCode, responseId, title, why, onStart, onStop, busy,
}: {
  /** `OFF` | `OPTIONAL` | `REQUIRED`, straight off the poll. */
  mode: string
  kindCode: ClipKind
  responseId: string | null
  title: string
  why: string
  onStart: () => void
  onStop: () => void
  /** True while the session is doing something that must not be interrupted. */
  busy?: boolean
}) {
  const rec = useRecorder()
  const elapsed = useElapsed(rec.startedAt)
  const preflight = micPreflight()
  const mine = rec.clips.filter((c) => c.kindCode === kindCode)
  const live = rec.phase === 'recording' && rec.kindCode === kindCode
  const otherLive = rec.phase === 'recording' && rec.kindCode !== kindCode

  if (mode === 'OFF') return null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">{title}</span>
        {mode === 'REQUIRED' && (
          <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-erp-xs text-amber-700">
            required
          </span>
        )}
      </div>
      <p className="text-erp-xs text-muted-foreground">{why}</p>

      {!preflight.ok && preflight.reason && <Note tone="bad">{preflight.reason}</Note>}
      {rec.micError && <Note tone="bad">{rec.micError}</Note>}

      <div className="flex flex-wrap items-center gap-2">
        {live ? (
          <button
            type="button"
            onClick={onStop}
            disabled={busy}
            className="erp-btn h-11 min-w-[10rem] border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
          >
            <Square className="h-4 w-4" /> Stop · {clock(elapsed)}
          </button>
        ) : (
          <button
            type="button"
            onClick={onStart}
            disabled={busy || !preflight.ok || otherLive || rec.phase === 'requesting'}
            className="erp-btn h-11 min-w-[10rem]"
          >
            {rec.phase === 'requesting'
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Mic className="h-4 w-4" />}
            {mine.length > 0 ? 'Record again' : 'Start recording'}
          </button>
        )}
        {/*
          Rule 3: disable only when the reason is visible. A `title` on a disabled
          button cannot be reached by a thumb, so the sentence sits next to it.
        */}
        {otherLive && (
          <span className="text-erp-xs text-muted-foreground">
            The consent statement is still recording. Stop that first — one microphone, one clip
            at a time.
          </span>
        )}
      </div>

      {mine.map((clip) => (
        <ClipRow key={clip.id} clip={clip} responseId={responseId} />
      ))}
    </div>
  )
}

function ClipRow({ clip, responseId }: { clip: HeldClip; responseId: string | null }) {
  const [retrying, setRetrying] = useState(false)

  async function retry() {
    if (!responseId) return
    // `uploadHeldClips` may already be walking this clip. Both it and this
    // handler flip the clip to `uploading` BEFORE their first await and skip a
    // clip already in that state, so the flag is the lock — but only if this
    // side checks it too. Without the check, pressing Retry while the automatic
    // pass is mid-flight uploads the same bytes twice and leaves a duplicate
    // clip on the interview for a coder to puzzle over.
    if (clip.state === 'uploading') return
    setRetrying(true)
    patchClip(clip.id, { state: 'uploading', message: null })
    const outcome = await uploadClip(responseId, clip)
    patchClip(clip.id, {
      state: outcome.state,
      message: outcome.message,
      mediaId: outcome.mediaId,
      // Same reason as `uploadHeldClips`: this button is pressed repeatedly on a
      // bad connection, and each press must not cost a RESERVED media row.
      uploadUrl: outcome.uploadUrl,
    })
    setRetrying(false)
  }

  const tone = clip.state === 'stored' ? 'text-emerald-700'
    : clip.state === 'refused' ? 'text-red-700'
      : clip.state === 'failed' ? 'text-amber-700' : 'text-muted-foreground'

  return (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-erp-sm">
        {clip.state === 'stored' && <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
        {clip.state === 'uploading' && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
        {(clip.state === 'failed' || clip.state === 'refused')
          && <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
        <span className={cn('font-medium', tone)}>
          {clip.state === 'held' ? 'Recorded, not yet uploaded'
            : clip.state === 'uploading' ? 'Uploading'
              : clip.state === 'stored' ? 'Stored'
                : clip.state === 'refused' ? 'Refused' : 'Upload failed'}
        </span>
        <span className="tabular-nums text-erp-xs text-muted-foreground">
          {clock(clip.durationSeconds)} · {(clip.sizeBytes / (1024 * 1024)).toFixed(1)} MB
        </span>
        {clip.state === 'failed' && responseId && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="erp-btn erp-btn-ghost h-8 text-erp-xs ml-auto"
          >
            <RefreshCw className="h-3 w-3" /> Retry upload
          </button>
        )}
      </div>
      {clip.message && <p className="text-erp-xs text-muted-foreground">{clip.message}</p>}
      {clip.state === 'held' && !responseId && (
        <p className="text-erp-xs text-muted-foreground">
          It goes up as soon as the interview is submitted — a recording needs an interview to
          belong to, and the interview is created first.
        </p>
      )}
    </div>
  )
}
